import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './AttendancePage.css';
import { getTodayAttendance } from '../services/googleScriptServiceCORS';
import Icon from './Icon';
import LoadingSpinner from './LoadingSpinner';
import { InlineStatus } from './UIState';
import { useToast } from './ToastProvider';
import { useLocation } from '../hooks/useLocation';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useAttendanceSubmit } from '../hooks/useAttendanceSubmit';

function AttendancePage({ employee, apiBaseUrl }) {
  const { showToast } = useToast();
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [requiresExplanation, setRequiresExplanation] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [inlineStatus, setInlineStatus] = useState({ type: '', message: '' });

  const {
    location,
    locationError,
    locationPermission,
    isRequestingLocation,
    projectLocations,
    requestLocation,
    refreshLocation,
  } = useLocation();

  const loadTodayAttendance = useCallback(async () => {
    try {
      const useGoogleScript = process.env.REACT_APP_GOOGLE_SCRIPT_URL;

      if (useGoogleScript) {
        const todayData = await getTodayAttendance(employee);
        setTodayAttendance(todayData);
        return;
      }

      const response = await axios.get(`${apiBaseUrl}/api/attendance/today/${employee.id}`);
      setTodayAttendance(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        setInlineStatus({ type: 'error', message: 'Không thể tải dữ liệu chấm công hôm nay.' });
      }
      setTodayAttendance(null);
    }
  }, [employee, apiBaseUrl]);

  useEffect(() => {
    loadTodayAttendance();
  }, [loadTodayAttendance]);

  useEffect(() => {
    if (location) {
      setValidationWarnings([]);
      setRequiresExplanation(false);
      setExplanation('');
    }
  }, [location]);

  const { offlineQueueCount, enqueueOfflineIfNeeded } = useOfflineSync({
    todayAttendance,
    showToast,
    setInlineStatus,
  });

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const checkValidation = (type) => {
    const warnings = [];
    let needExplanation = false;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    if (type === 'checkin' && currentTime > 8 * 60 + 30) {
      warnings.push('Giờ vào sau 8h30');
      needExplanation = true;
    }

    if (type === 'checkout' && currentTime < 16 * 60 + 30) {
      warnings.push('Giờ ra trước 16h30');
      needExplanation = true;
    }

    if (type === 'checkin' && location && projectLocations.length > 0) {
      let inRange = false;
      let minDistance = Infinity;
      let nearest = null;

      projectLocations.forEach((project) => {
        if (!project.latitude || !project.longitude || !project.radius) return;
        const distance = calculateDistance(location.latitude, location.longitude, project.latitude, project.longitude);

        if (distance <= project.radius) {
          inRange = true;
          return;
        }

        if (distance < minDistance) {
          minDistance = distance;
          nearest = project;
        }
      });

      if (!inRange) {
        const nearestText = nearest ? `cách ${Math.round(minDistance)}m từ ${nearest.name}` : 'ngoài khu vực làm việc';
        warnings.push(`Ngoài khu vực làm việc (${nearestText})`);
        needExplanation = true;
      }
    }

    setValidationWarnings(warnings);
    setRequiresExplanation(needExplanation);
    return { warnings, needExplanation };
  };

  const { loading, handleCheckin, handleCheckout } = useAttendanceSubmit({
    employee,
    apiBaseUrl,
    location,
    image,
    explanation,
    requiresExplanation,
    validationWarnings,
    setValidationWarnings,
    setRequiresExplanation,
    setExplanation,
    setImage,
    setImagePreview,
    setTodayAttendance,
    loadTodayAttendance,
    checkValidation,
    setInlineStatus,
    showToast,
    enqueueOfflineIfNeeded,
  });

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const { compressImageForAttendance } = await import('../utils/imageCompression');
      const compressed = await compressImageForAttendance(file, null);
      setImage(compressed);

      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(compressed);
    } catch {
      setImage(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const canCheckin = !todayAttendance || !todayAttendance.checkin_time;
  const canCheckout = Boolean(location && image && (!todayAttendance || !todayAttendance.checkout_time));

  const imageSizeMb = image ? image.size / (1024 * 1024) : 0;
  const imageQualityHint = !image
    ? 'Chưa có ảnh.'
    : imageSizeMb <= 1.5
      ? 'Ảnh nhẹ, tốc độ tải tốt.'
      : imageSizeMb <= 3
        ? 'Ảnh ổn, có thể mất thêm thời gian tải.'
        : 'Ảnh lớn, dễ trễ trên mạng yếu.';

  return (
    <div className="attendance-page">
      <div className="attendance-card">
        <InlineStatus type={inlineStatus.type || 'info'} message={inlineStatus.message} />

        {offlineQueueCount > 0 ? (
          <div className="offline-queue-banner">Đang có {offlineQueueCount} bản ghi offline chưa đồng bộ.</div>
        ) : null}


        <h2>Chấm Công Hôm Nay</h2>

        {todayAttendance ? (
          <div className="attendance-info">
            {todayAttendance.checkin_time ? (
              <div className="info-item">
                <span className="label">Giờ vào:</span>
                <span className="value">{new Date(todayAttendance.checkin_time).toLocaleTimeString('vi-VN')}</span>
              </div>
            ) : null}
            {todayAttendance.checkout_time ? (
              <div className="info-item">
                <span className="label">Giờ ra:</span>
                <span className="value">{new Date(todayAttendance.checkout_time).toLocaleTimeString('vi-VN')}</span>
              </div>
            ) : null}
            {todayAttendance.work_hours ? (
              <div className="info-item">
                <span className="label">Tổng giờ:</span>
                <span className="value">{todayAttendance.work_hours.toFixed(2)} giờ</span>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="location-section">
          <h3>
            <Icon name="location" size={20} className="icon-primary" />
            <span>Vị trí hiện tại</span>
          </h3>

          {location ? (
            <div className="location-info">
              <p className="location-address">{location.address || 'Đang cập nhật địa chỉ...'}</p>
              <p className="location-coords">
                {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
              </p>
            </div>
          ) : null}

          {locationError ? <div className="location-error-box"><p className="location-error-text">{locationError}</p></div> : null}

          {locationPermission === 'prompt' || locationPermission === 'denied' || !location ? (
            <button onClick={requestLocation} className="btn-request-location" disabled={isRequestingLocation}>
              {isRequestingLocation ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Đang yêu cầu...</span>
                </>
              ) : (
                <>
                  <Icon name="location" size={18} />
                  <span>Xin quyền vị trí</span>
                </>
              )}
            </button>
          ) : null}

          {location ? (
            <button onClick={refreshLocation} className="btn-refresh-location" disabled={isRequestingLocation}>
              Làm mới vị trí
            </button>
          ) : null}
        </div>

        <div className="image-section">
          <h3>
            <Icon name="camera" size={20} className="icon-primary" />
            <span>Ảnh chấm công</span>
          </h3>
          <div className="image-input-options">
            <input type="file" accept="image/*" capture="environment" onChange={handleImageChange} className="image-input" id="attendance-image" />
            <label htmlFor="attendance-image" className="image-input-label">
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="image-preview" />
              ) : (
                <div className="image-placeholder">
                  <span>Chọn ảnh</span>
                </div>
              )}
            </label>
          </div>

          {image ? (
            <button
              onClick={() => {
                setImage(null);
                setImagePreview(null);
              }}
              className="btn-remove-image"
            >
              Xóa ảnh
            </button>
          ) : null}

          <p className="image-quality-hint">{imageQualityHint}</p>
        </div>

        {requiresExplanation ? (
          <>
            {validationWarnings.length > 0 ? (
              <div className="explanation-warning" role="alert">
                <h3>
                  <Icon name="warning" size={20} className="icon-warning" />
                  <span>Cần giải trình</span>
                </h3>
                <ul className="warning-list">
                  {validationWarnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="explanation-section">
              <h3>
                <Icon name="info" size={20} className="icon-info" />
                <span>Giải trình <span className="required">*</span></span>
              </h3>
              <textarea
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                placeholder="Nhập giải trình..."
                className="explanation-input"
                rows="3"
                required
              />
            </div>
          </>
        ) : null}

        <div className="action-buttons">
          <button onClick={handleCheckin} disabled={loading || !canCheckin} className={`btn-checkin ${!canCheckin ? 'disabled' : ''}`}>
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Đang xử lý...</span>
              </>
            ) : (
              <>
                <Icon name="checkCircle" size={20} />
                <span>Chấm Công Vào</span>
              </>
            )}
          </button>
          <button onClick={handleCheckout} disabled={loading || !canCheckout} className={`btn-checkout ${!canCheckout ? 'disabled' : ''}`}>
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Đang xử lý...</span>
              </>
            ) : (
              <>
                <Icon name="logout" size={20} />
                <span>Chấm Công Ra</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AttendancePage;

